import { useState, Fragment } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, PackageCheck, X, Calculator } from "lucide-react";
import { formatMoney } from "@finora/config";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { DateInputDDMMYYYY } from "../../components/DateInputDDMMYYYY";
import { ProductAutocomplete } from "../../components/ProductAutocomplete";
import { CalculationModal } from "./CalculationModal";

interface DraftItem {
  productId: string | null;
  sku: string;
  barcode: string;
  serialNumber: string;
  name: string;
  quantity: string;
  unitPrice: string;
  discountPercent: string;
  taxRatePercent: string;
  hasExpiry: boolean;
  expiryMode: "days" | "date";
  shelfLifeDays: string;
  expiryDate: string;
}

function emptyItem(): DraftItem {
  return {
    productId: null,
    sku: "",
    barcode: "",
    serialNumber: "",
    name: "",
    quantity: "1",
    unitPrice: "",
    discountPercent: "0",
    taxRatePercent: "20",
    hasExpiry: false,
    expiryMode: "date",
    shelfLifeDays: "",
    expiryDate: "",
  };
}

function resolveExpiryDate(item: DraftItem, receiptDate: string): string | null {
  if (!item.hasExpiry) return null;
  if (item.expiryMode === "date") {
    return item.expiryDate || null;
  }
  const days = Number(item.shelfLifeDays);
  if (!item.shelfLifeDays || Number.isNaN(days)) return null;
  const base = receiptDate ? new Date(receiptDate) : new Date();
  base.setDate(base.getDate() + days);
  return base.toISOString().slice(0, 10);
}

function lineAmount(item: DraftItem): number {
  const qty = Number(item.quantity.replace(",", "."));
  const price = Number(item.unitPrice.replace(",", "."));
  const discount = Number((item.discountPercent || "0").replace(",", "."));
  if (Number.isNaN(qty) || Number.isNaN(price) || Number.isNaN(discount)) return 0;
  return qty * price * (1 - discount / 100);
}

function NewGoodsReceiptModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [supplierId, setSupplierId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [supplierDeliveryNoteNumber, setSupplierDeliveryNoteNumber] = useState("");
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [items, setItems] = useState<DraftItem[]>([emptyItem()]);

  const { data: suppliers } = useQuery({ queryKey: ["suppliers"], queryFn: () => api.listSuppliers() });
  const { data: warehouses } = useQuery({ queryKey: ["warehouses"], queryFn: () => api.listWarehouses() });

  const defaultWarehouseId = warehouses?.find((w) => w.isDefault)?.id ?? warehouses?.[0]?.id ?? "";
  const effectiveWarehouseId = warehouseId || defaultWarehouseId;

  const total = items.reduce((sum, item) => sum + lineAmount(item), 0);

  const save = useMutation({
    mutationFn: () =>
      api.createGoodsReceipt({
        supplierId,
        warehouseId: effectiveWarehouseId,
        supplierDeliveryNoteNumber: supplierDeliveryNoteNumber || undefined,
        receiptDate: receiptDate || undefined,
        dueDate: dueDate || undefined,
        items: items
          .filter((i) => i.name.trim() && i.quantity.trim() && i.unitPrice.trim())
          .map((i) => ({
            productId: i.productId,
            sku: i.sku || undefined,
            barcode: i.barcode || undefined,
            serialNumber: i.serialNumber || undefined,
            name: i.name,
            quantity: i.quantity.replace(",", "."),
            unitPrice: i.unitPrice.replace(",", "."),
            discountPercent: i.discountPercent.replace(",", "."),
            taxRatePercent: i.taxRatePercent.replace(",", "."),
            expiryDate: resolveExpiryDate(i, receiptDate),
          })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goods-receipts"] });
      queryClient.invalidateQueries({ queryKey: ["stock-levels"] });
      onClose();
    },
  });

  function updateItem(idx: number, patch: Partial<DraftItem>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  const canSubmit =
    !!supplierId &&
    !!effectiveWarehouseId &&
    items.some((i) => i.name.trim() && i.quantity.trim() && i.unitPrice.trim());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-auto rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Prijem robe</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-red-600">Dobavljač *</label>
              <select
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              >
                <option value="">Izaberi...</option>
                {suppliers?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Magacin</label>
              <select
                value={effectiveWarehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
                className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              >
                {warehouses?.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Broj otpremnice dobavljača
              </label>
              <input
                value={supplierDeliveryNoteNumber}
                onChange={(e) => setSupplierDeliveryNoteNumber(e.target.value)}
                className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Valuta plaćanja</label>
              <DateInputDDMMYYYY value={dueDate} onChange={setDueDate} />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Datum prijema</label>
            <div className="w-40">
              <DateInputDDMMYYYY value={receiptDate} onChange={setReceiptDate} />
            </div>
          </div>

          <div>
            <table>
              <thead>
                <tr>
                  <th>Artikal (za lager)</th>
                  <th>Šifra</th>
                  <th>Naziv</th>
                  <th>Količina</th>
                  <th>Cena</th>
                  <th>Rabat %</th>
                  <th>PDV %</th>
                  <th>Iznos</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <Fragment key={idx}>
                  <tr>
                    <td className={item.productId ? "w-40" : "min-w-[220px]"}>
                      <ProductAutocomplete
                        value={item.productId ? item.name : ""}
                        placeholder="(ručni unos - kucaj za pretragu)"
                        onSelect={(product) =>
                          updateItem(idx, {
                            productId: product.id,
                            sku: product.sku,
                            name: product.name,
                            unitPrice: product.purchasePrice ?? item.unitPrice,
                          })
                        }
                      />
                    </td>
                    <td className="w-24">
                      <input
                        value={item.sku}
                        onChange={(e) => updateItem(idx, { sku: e.target.value })}
                        className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                      />
                    </td>
                    <td className="min-w-[220px]">
                      <input
                        value={item.name}
                        onChange={(e) => updateItem(idx, { name: e.target.value })}
                        className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                      />
                    </td>
                    <td className="w-24">
                      <input
                        value={item.quantity}
                        onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                        className="h-9 w-full rounded-lg border border-border px-2 text-right text-sm focus:border-brand focus:outline-none"
                      />
                    </td>
                    <td className="w-28">
                      <input
                        value={item.unitPrice}
                        onChange={(e) => updateItem(idx, { unitPrice: e.target.value })}
                        className="h-9 w-full rounded-lg border border-border px-2 text-right text-sm focus:border-brand focus:outline-none"
                      />
                    </td>
                    <td className="w-24">
                      <input
                        value={item.discountPercent}
                        onChange={(e) => updateItem(idx, { discountPercent: e.target.value })}
                        className="h-9 w-full rounded-lg border border-border px-2 text-right text-sm focus:border-brand focus:outline-none"
                      />
                    </td>
                    <td className="w-20">
                      <input
                        value={item.taxRatePercent}
                        onChange={(e) => updateItem(idx, { taxRatePercent: e.target.value })}
                        className="h-9 w-full rounded-lg border border-border px-2 text-right text-sm focus:border-brand focus:outline-none"
                      />
                    </td>
                    <td className="w-28 text-right text-sm font-medium">{formatMoney(lineAmount(item).toFixed(2))}</td>
                    <td>
                      <button
                        aria-label="Ukloni stavku"
                        onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                        className="text-slate-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    </td>
                  </tr>
                  <tr key={`${idx}-expiry`}>
                    <td colSpan={8} className="pb-2">
                      {!item.productId && item.name.trim() && (
                        <p className="mb-1 text-xs text-amber-600">
                          Nije izabran artikal iz kataloga - sistem će ga pronaći po nazivu, šifri ili bar
                          kodu, ili napraviti nov (izaberite iz liste "Artikal (za lager)" iznad ako želite
                          da unapred povežete ovu stavku).
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-3">
                        <div>
                          <label className="mb-0.5 block text-xs text-slate-500">Bar kod</label>
                          <input
                            value={item.barcode}
                            onChange={(e) => updateItem(idx, { barcode: e.target.value })}
                            className="h-8 w-36 rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="mb-0.5 block text-xs text-slate-500">Serijski broj</label>
                          <input
                            value={item.serialNumber}
                            onChange={(e) => updateItem(idx, { serialNumber: e.target.value })}
                            className="h-8 w-36 rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none"
                          />
                        </div>
                      </div>
                      <label className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                        <input
                          type="checkbox"
                          checked={item.hasExpiry}
                          onChange={(e) => updateItem(idx, { hasExpiry: e.target.checked })}
                          className="h-3.5 w-3.5 rounded border-border text-brand focus:ring-brand"
                        />
                        Rok trajanja / datum isteka
                      </label>
                      {item.hasExpiry && (
                        <div className="mt-1 flex items-center gap-3 pl-5">
                          <label className="flex items-center gap-1 text-xs text-slate-500">
                            <input
                              type="radio"
                              checked={item.expiryMode === "days"}
                              onChange={() => updateItem(idx, { expiryMode: "days" })}
                            />
                            Rok trajanja (dana)
                          </label>
                          <input
                            disabled={item.expiryMode !== "days"}
                            value={item.shelfLifeDays}
                            onChange={(e) => updateItem(idx, { shelfLifeDays: e.target.value })}
                            placeholder="npr. 365"
                            className="h-8 w-24 rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none disabled:bg-app-bg disabled:text-slate-300"
                          />
                          <label className="flex items-center gap-1 text-xs text-slate-500">
                            <input
                              type="radio"
                              checked={item.expiryMode === "date"}
                              onChange={() => updateItem(idx, { expiryMode: "date" })}
                            />
                            Datum isteka
                          </label>
                          <div className={item.expiryMode !== "date" ? "pointer-events-none opacity-40" : ""}>
                            <DateInputDDMMYYYY
                              value={item.expiryDate}
                              onChange={(iso) => updateItem(idx, { expiryDate: iso })}
                            />
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
            <button
              onClick={() => setItems((prev) => [...prev, emptyItem()])}
              className="mt-2 flex items-center gap-1 text-sm text-brand hover:underline"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden /> Dodaj stavku
            </button>
          </div>

          <div className="flex justify-end border-t border-border pt-4">
            <div className="text-right">
              <p className="text-sm text-slate-500">Ukupno</p>
              <p className="text-lg font-semibold text-slate-900">{formatMoney(total.toFixed(2))}</p>
            </div>
          </div>

          {save.isError && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              Čuvanje nije uspelo. Proverite unete podatke.
            </p>
          )}
        </div>

        <div className="flex gap-3 border-t border-border px-6 py-4">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Otkaži
          </Button>
          <Button className="flex-1" disabled={!canSubmit || save.isPending} onClick={() => save.mutate()}>
            Sačuvaj prijem
          </Button>
        </div>
      </div>
    </div>
  );
}

function GoodsReceiptDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: receipt, isLoading } = useQuery({
    queryKey: ["goods-receipt", id],
    queryFn: () => api.getGoodsReceipt(id),
  });
  const [showCalculation, setShowCalculation] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-auto rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Prijemnica</h2>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setShowCalculation(true)}>
              <Calculator className="h-3.5 w-3.5" aria-hidden /> Kalkulacija
            </Button>
            <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>
        </div>
        {isLoading || !receipt ? (
          <p className="p-6 text-sm text-slate-500">Učitavanje...</p>
        ) : (
          <div className="space-y-4 px-6 py-5">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-400">Dobavljač</p>
                <p className="font-medium text-slate-800">{receipt.supplierName}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Magacin</p>
                <p className="font-medium text-slate-800">{receipt.warehouseName}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Datum prijema</p>
                <p className="text-slate-700">{new Date(receipt.receiptDate).toLocaleDateString("sr-RS")}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Valuta plaćanja</p>
                <p className="text-slate-700">
                  {receipt.dueDate ? new Date(receipt.dueDate).toLocaleDateString("sr-RS") : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Broj otpremnice dobavljača</p>
                <p className="text-slate-700">{receipt.supplierDeliveryNoteNumber ?? "—"}</p>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Šifra</th>
                  <th>Naziv</th>
                  <th>Količina</th>
                  <th>Cena</th>
                  <th>Rabat %</th>
                  <th>Iznos</th>
                </tr>
              </thead>
              <tbody>
                {receipt.items.map((item) => (
                  <tr key={item.id}>
                    <td className="text-slate-500">{item.sku ?? "—"}</td>
                    <td className="font-medium text-slate-800">
                      {item.name}
                      {item.serialNumber && (
                        <div className="text-xs text-slate-400">S/N: {item.serialNumber}</div>
                      )}
                      {item.expiryDate && (
                        <div className="text-xs text-slate-400">
                          Rok trajanja: {new Date(item.expiryDate).toLocaleDateString("sr-RS")}
                        </div>
                      )}
                    </td>
                    <td>{item.quantity}</td>
                    <td>{formatMoney(item.unitPrice)}</td>
                    <td>{item.discountPercent}%</td>
                    <td>{formatMoney(item.netAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex justify-end border-t border-border pt-3">
              <p className="text-lg font-semibold text-slate-900">{formatMoney(receipt.grossAmount)}</p>
            </div>
          </div>
        )}
      </div>
      {showCalculation && (
        <CalculationModal goodsReceiptId={id} onClose={() => setShowCalculation(false)} />
      )}
    </div>
  );
}

export function ReceiptsTab() {
  const [modalOpen, setModalOpen] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const { data: receipts, isLoading } = useQuery({
    queryKey: ["goods-receipts"],
    queryFn: () => api.listGoodsReceipts(),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setModalOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Novi prijem robe
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !receipts || receipts.length === 0 ? (
          <div className="p-5">
            <EmptyState
              icon={PackageCheck}
              title="Još nema prijema robe"
              description="Evidentirajte prvi prijem robe dugmetom iznad."
            />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Datum</th>
                <th>Dobavljač</th>
                <th>Magacin</th>
                <th>Broj otpremnice</th>
                <th>Iznos</th>
              </tr>
            </thead>
            <tbody>
              {receipts.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setViewingId(r.id)}>
                  <td className="text-slate-500">{new Date(r.receiptDate).toLocaleDateString("sr-RS")}</td>
                  <td className="font-medium text-slate-800">{r.supplierName}</td>
                  <td className="text-slate-500">{r.warehouseName}</td>
                  <td className="text-slate-500">{r.supplierDeliveryNoteNumber ?? "—"}</td>
                  <td>{formatMoney(r.grossAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && <NewGoodsReceiptModal onClose={() => setModalOpen(false)} />}
      {viewingId && <GoodsReceiptDetailModal id={viewingId} onClose={() => setViewingId(null)} />}
    </div>
  );
}
